import { cn } from "@/lib/utils";
import {
  LIST_TAG_PILL_MAX,
  overflowCount,
  softTagStyle,
} from "../tag-pill-style";

const pillBase =
  "inline-flex max-w-full items-center rounded-full border px-2 py-0.5 text-[11px] font-medium leading-tight";

/** Soft rounded-full tag label — plan/35 (doc header + lists). */
export function TagPill(props: {
  name: string;
  color?: string | null;
  /** Selected / active filter weight. */
  active?: boolean;
  className?: string;
  density?: "list" | "header";
}) {
  return (
    <span
      className={cn(
        pillBase,
        props.density === "header" && "px-2.5 py-1 text-xs",
        props.active && "ring-2 ring-[var(--color-ring)] ring-offset-1",
        props.className,
      )}
      style={softTagStyle(props.color)}
      title={props.name}
    >
      <span className="truncate">{props.name}</span>
    </span>
  );
}

/** Clickable list tag — filters by tag. */
export function FilterTagPill(props: {
  name: string;
  color?: string | null;
  active?: boolean;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      title={`Filter: ${props.name}`}
      onClick={props.onClick}
      className={cn(
        "cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]",
        props.className,
      )}
    >
      <TagPill
        name={props.name}
        color={props.color}
        active={props.active}
        density="list"
        className="pointer-events-none"
      />
    </button>
  );
}

export type ListTag = { id: string; name: string; color?: string | null };

/** Row tags with +N overflow — plan/35. */
export function ListTagPills(props: {
  tags: ListTag[];
  activeTagId?: string;
  onFilterTag: (tagId: string) => void;
  maxVisible?: number;
}) {
  const max = props.maxVisible ?? LIST_TAG_PILL_MAX;
  if (props.tags.length === 0) {
    return <span className="text-[var(--color-muted)]">—</span>;
  }
  const visible = props.tags.slice(0, max);
  const more = overflowCount(props.tags.length, max);
  const hiddenNames = props.tags
    .slice(max)
    .map((t) => t.name)
    .join(", ");
  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((t) => (
        <FilterTagPill
          key={t.id}
          name={t.name}
          color={t.color}
          active={props.activeTagId === t.id}
          onClick={() => props.onFilterTag(t.id)}
        />
      ))}
      {more > 0 ? (
        <span
          className={cn(pillBase, "border-[var(--color-border)] text-[var(--color-muted)]")}
          title={hiddenNames}
        >
          +{more}
        </span>
      ) : null}
    </div>
  );
}
